// Lazy fetcher for Sleeper weekly NFL stats with a localStorage cache.
// Sleeper's public API doesn't document these endpoints but they're stable,
// so be polite: slim each response down and cache aggressively.

const SEASON = 2025; // most recent completed season as of May 2026
const NUM_WEEKS = 18;
const TTL_MS = 6 * 60 * 60 * 1000; // 6h — stats for a finished season don't churn
const CACHE_KEY = `sleeper:stats:nfl:regular:${SEASON}`;

// Per-week shape we cache: { playerId: { ppr, gp } }
// Original Sleeper payload is ~560KB; this trims to ~30KB per week.
function slimWeek(raw) {
  const slim = {};
  for (const [pid, stats] of Object.entries(raw)) {
    if (stats?.pts_ppr == null) continue;
    slim[pid] = {
      ppr: Math.round(stats.pts_ppr * 100) / 100,
      gp: stats.gp ?? 0,
    };
  }
  return slim;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { savedAt, weeks } = JSON.parse(raw);
    if (Date.now() - savedAt > TTL_MS) return null;
    return weeks;
  } catch {
    return null;
  }
}

function writeCache(weeks) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), weeks })
    );
  } catch {
    // quota exceeded or storage disabled — silent ok, we just won't cache
  }
}

// In-flight promise so we only ever fire one set of fetches per session
let inFlight = null;

async function fetchAllWeeks() {
  const weeks = await Promise.all(
    Array.from({ length: NUM_WEEKS }, (_, i) =>
      fetch(
        `https://api.sleeper.app/v1/stats/nfl/regular/${SEASON}/${i + 1}`
      )
        .then((r) => (r.ok ? r.json() : {}))
        .then(slimWeek)
        .catch(() => ({}))
    )
  );
  return weeks;
}

export function getSeason() {
  return SEASON;
}

export async function loadSeasonStats() {
  const cached = readCache();
  if (cached) return cached;
  if (inFlight) return inFlight;
  inFlight = (async () => {
    const weeks = await fetchAllWeeks();
    writeCache(weeks);
    return weeks;
  })();
  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

export function getPlayerWeeks(weeks, playerId) {
  if (!weeks || !playerId) return [];
  return weeks.map((w, i) => ({
    week: i + 1,
    ppr: w?.[playerId]?.ppr ?? null,
    gp: w?.[playerId]?.gp ?? 0,
  }));
}

export function summarizePlayer(weekly) {
  let total = 0;
  let gp = 0;
  let best = null;
  for (const w of weekly) {
    if (w.ppr == null) continue;
    total += w.ppr;
    if (w.gp > 0) gp += 1;
    if (best == null || w.ppr > best.ppr) best = w;
  }
  return {
    total: Math.round(total * 100) / 100,
    gp,
    avg: gp > 0 ? Math.round((total / gp) * 100) / 100 : 0,
    best,
  };
}
